/**
 * Web-specific API client implementation using axios
 */

import { BaseApiClient } from './base-client';
import { WebClientConfig, RequestOptions, ApiClientResponse } from './types';

// Axios types (will be provided by peer dependency)
interface AxiosResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
}

interface AxiosInstance {
  request<T = any>(config: any): Promise<AxiosResponse<T>>;
}

interface AxiosStatic {
  create(config?: any): AxiosInstance;
}

// Dynamic axios import to handle peer dependency
let axios: AxiosStatic;

export class WebApiClient extends BaseApiClient {
  private axiosInstance: AxiosInstance | null = null;

  constructor(config: WebClientConfig) {
    super(config);
    this.initializeAxios();
  }

  private async initializeAxios() {
    try {
      // Dynamic import of axios peer dependency
      const axiosModule = await import('axios');
      axios = axiosModule.default || axiosModule;
      
      this.axiosInstance = axios.create({
        baseURL: this.config.baseURL,
        timeout: this.config.timeout || 30000,
        headers: {
          'Content-Type': 'application/json',
          ...this.config.defaultHeaders,
        },
      });
    } catch (error) {
      throw new Error('Axios is required for WebApiClient. Please install axios as a peer dependency.');
    }
  }

  protected async makeRequest<T = any>(
    url: string, 
    options?: RequestOptions
  ): Promise<ApiClientResponse<T>> {
    if (!this.axiosInstance) {
      await this.initializeAxios();
    }

    try {
      const axiosConfig = {
        url,
        method: options?.method || 'GET',
        headers: options?.headers,
        data: options?.body,
        timeout: options?.timeout,
        signal: options?.signal,
      };

      const response = await this.axiosInstance!.request<T>(axiosConfig);

      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: this.normalizeHeaders(response.headers),
        ok: response.status >= 200 && response.status < 300,
      };
    } catch (error: any) {
      // Handle axios errors
      if (error.response) {
        return {
          data: error.response.data,
          status: error.response.status,
          statusText: error.response.statusText,
          headers: this.normalizeHeaders(error.response.headers),
          ok: false,
        };
      }
      
      // Network or other errors
      throw error;
    }
  }

  private normalizeHeaders(headers: any): Record<string, string> {
    const normalized: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers || {})) {
      normalized[key.toLowerCase()] = String(value);
    }
    return normalized;
  }
}

/**
 * Factory function to create a web API client
 */
export function createWebApiClient(config: Omit<WebClientConfig, 'platform'>): WebApiClient {
  return new WebApiClient({ ...config, platform: 'web' });
} 